import * as Sentry from '@sentry/vue'
import { defineStore } from 'pinia'
import { McsControllerApi } from '~/api/prebooking'
import { createDate } from '~/composables/prebooking-date'
import { useTranslations } from '~/composables/translate/translations'
import { DATE_TRANSLATIONS_OPTIONS } from '~/stores/datetime'
import { useLocationStore } from '~/stores/location'

const FALLBACK_MIN_ADVANCE_DAYS = 5
const FALLBACK_MIN_MONTHS = 2
const FALLBACK_MAX_MONTHS = 18
// Not returned by the MCS config endpoint - how far in the future a start date can be booked
const MAX_ADVANCE_DAYS = 365

function getStartDateBounds(minAdvanceDays: number): { min: Date, max: Date } {
  const now = new Date()
  const min = new Date(now)
  min.setDate(now.getDate() + minAdvanceDays)
  const max = new Date(now)
  max.setDate(now.getDate() + MAX_ADVANCE_DAYS)
  return { min, max }
}

export const useSubscriptionDateTimeStore = defineStore('subscription-date-time', () => {
  const { min, max } = getStartDateBounds(FALLBACK_MIN_ADVANCE_DAYS)

  const minStartingDate = ref(createDate(min, { minutesRounding: 'later' }).date!)
  const maxStartingDate = ref(createDate(max, { minutesRounding: 'sooner' }).date!)
  const minimumMonths = ref(FALLBACK_MIN_MONTHS)
  const maximumMonths = ref(FALLBACK_MAX_MONTHS)

  const startDateTime = ref<Date>(new Date(minStartingDate.value))
  const durationMonths = ref<number | undefined>(undefined)

  const locationStore = useLocationStore()

  async function loadConfig(): Promise<void> {
    try {
      const mcsConfig = await new McsControllerApi()
        .getMcsConfig({ locationId: locationStore.city.locationId })

      const { min: newMin, max: newMax } = getStartDateBounds(mcsConfig.minimumDeliveryLeadDays)
      minStartingDate.value = createDate(newMin, { minutesRounding: 'later' }).date!
      maxStartingDate.value = createDate(newMax, { minutesRounding: 'sooner' }).date!
      minimumMonths.value = mcsConfig.minimumMonths
      maximumMonths.value = mcsConfig.maximumMonths
      startDateTime.value = new Date(minStartingDate.value)
    }
    catch (error) {
      Sentry.captureException(error)
      console.error('Error while loading MCS configuration', error)
    }
  }

  const errorStartingDate = computed<string | undefined>(() => {
    const language = useTranslations().language.value

    if (startDateTime.value < minStartingDate.value) {
      return translate(
        'error_too_soon_to_prebook',
        { '{DATE}': minStartingDate.value.toLocaleDateString(language, DATE_TRANSLATIONS_OPTIONS) },
      )
    }

    if (startDateTime.value > maxStartingDate.value) {
      return translate(
        'error_prebook_start_too_far_away',
        { '{DATE}': maxStartingDate.value.toLocaleDateString(language, DATE_TRANSLATIONS_OPTIONS) },
      )
    }

    return undefined
  })

  const endDateTime = computed<Date | undefined>(() => {
    if (durationMonths.value == null)
      return undefined
    const start = startDateTime.value
    const targetMonth = start.getMonth() + durationMonths.value
    const end = new Date(start)
    end.setDate(1)
    end.setMonth(targetMonth)
    const daysInTargetMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate()
    end.setDate(Math.min(start.getDate(), daysInTargetMonth))
    return end
  })

  const isValid = computed(() => !errorStartingDate.value && durationMonths.value != null)

  return { startDateTime, durationMonths, minStartingDate, maxStartingDate, minimumMonths, maximumMonths, errorStartingDate, isValid, endDateTime, loadConfig }
})
